import { Injectable, Input, Output, EventEmitter, Directive } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormGroup, AbstractControl, Validators, UntypedFormControl } from '@angular/forms';
import { CustomerCardModel } from './models/CardModel';
import { CardPaymentModel } from './models/RegisterModel';


@Directive()
@Injectable()
export class ModalBase<T> {

    @Input() readonly: boolean;

    @Output() onSave: EventEmitter<T> = new EventEmitter<T>();
    @Output() onCancel: EventEmitter<void> = new EventEmitter<void>();

    @Input() saveTitle = 'Guardar';
    @Input() cancelTitle = 'Cancelar';

    constructor(public modalService: NgbModal) {
    }

    open(content: any, options?: NgbModalOptions): NgbModalRef {

        return this.modalService.open(content, { ...options, backdrop: 'static', keyboard: false, windowClass: 'modal-full' });
    }

    public close(): void {

        this.modalService.dismissAll();
        this.onCancel.emit();
    }
}

@Directive()
@Injectable()
export class ModalFormBase<T> extends ModalBase<T> {

    @Input() touched = true;

    constructor(public modalService: NgbModal) {
        super(modalService);
    }

    protected _formGet(form: UntypedFormGroup, propName: string): AbstractControl | null {

        return form ? form.get(propName) : null;
    }

    protected _isInvalid(form: UntypedFormGroup, propName: string, touched: boolean = false): boolean {

        if (!form) {
            return true;
        }

        if (this.readonly) {
            return false;
        }

        const prop = form.get(propName);
        let invalid = prop && prop.invalid;
        if (touched) {
            invalid = invalid && prop.touched;
        }
        return invalid;
    }

    protected _changeReadonly(form: UntypedFormGroup): void {

        for (const key in form.controls) {

            if (this.readonly) {
                form.controls[key].disable();
            } else {
                form.controls[key].enable();
            }
        }
    }
}

@Directive()
@Injectable()
export class ModalFormPurchaseBase<T> extends ModalBase<T> {

    @Input() touched = true;


    // form
    formGroup: UntypedFormGroup;

    isAmexCVC = false;
    dynamicMask = '999';

    methods: CustomerCardModel[] = [];
    currentMethod: CustomerCardModel;

    selectedMethod = '';

    newCard: CardPaymentModel;
    requireInvoice = false;


    constructor(public modalService: NgbModal) {
        super(modalService);
    }

    protected _formGet(form: UntypedFormGroup, propName: string): AbstractControl | null {

        return form ? form.get(propName) : null;
    }

    protected _isInvalid(form: UntypedFormGroup, propName: string, touched: boolean = false): boolean {

        if (!form) {
            return true;
        }

        if (this.readonly) {
            return false;
        }

        const prop = form.get(propName);
        let invalid = prop && prop.invalid;
        if (touched) {
            invalid = invalid && prop.touched;
        }
        return invalid;
    }

    protected _changeReadonly(form: UntypedFormGroup): void {

        for (const key in form.controls) {

            if (this.readonly) {
                form.controls[key].disable();
            } else {
                form.controls[key].enable();
            }
        }
    }


    onSelectMethod(card: CustomerCardModel, modal: any) {

        this.currentMethod = card;
        modal.dismiss();
    }

    onOpenMethods(paymentMethodModal: any, isDarkTheme: boolean = true) {

        let windowsClass = isDarkTheme ? 'modal-dialog-end dark-theme modal-full' : 'modal-dialog-end modal-full';

        this.modalService.open(paymentMethodModal, {
            backdrop: 'static', keyboard: false,
            windowClass: windowsClass
        });
    }

    onOpenCreateCard(paymentMethodModal: any, isDarkTheme: boolean = true) {

        let windowsClass = isDarkTheme ? 'modal-dialog-end dark-theme modal-full' : 'modal-dialog-end modal-full';
        this.newCard = new CardPaymentModel();

        this.formInit();

        this.modalService.open(paymentMethodModal, {
            backdrop: 'static', keyboard: false,
            windowClass: windowsClass
        });
    }

    // Form

    onChangeCardNumber(ev: any) {

        const cardNumber = ev.target.value;

        const number = Number(cardNumber.substring(0, 2));

        // Se valida si la tarjeta ingresada es amex o no para determinar la mask de cvc
        if (number == 34 || number == 37) {

            this.isAmexCVC = true;

            // se cambia de mascara
            this.dynamicMask = '9999';
            this.cvc.setValue('');

        } else {

            // se cambia de mascara
            this.dynamicMask = '999';

            // Se valida si paso de amex a otro tipo para limpiar la variable cvc
            if (this.isAmexCVC) {

                this.cvc.setValue('');
            }

            this.isAmexCVC = false;
        }

        // Enviá la información de los inputs al padre para hacer validación dinámica
        this.dynamicInputValidation();
    }

    onChangeCVC(ev: any) {

        // Enviá la información de los inputs al padre para hacer validación dinámica
        this.dynamicInputValidation();
    }

    onChangeCardExpiry(ev: any) {

        const val = ev.target.value;
        let temp_val = '';

        // Enviá la información de los inputs al padre para hacer validación dinámica
        this.dynamicInputValidation();

        if (!isNaN(val)) {

            if (val > 1 && val < 10 && val.length == 1) {

                temp_val = '0' + val + '/';
                this.expiry.setValue(temp_val);
            } else if (val >= 1 && val < 10 && val.length == 2 && ev.keyCode != 8) {

                temp_val = val + '/';
                this.expiry.setValue(temp_val);
            } else if (val > 9 && val.length == 2 && ev.keyCode != 8) {

                temp_val = val + '/';
                this.expiry.setValue(temp_val);
            }
        }
        const valid = (val.length < 7) || ev.which == 8;
        return valid;
    }


    get expiry() { return this._formGet(this.formGroup, 'expiry'); }
    get invalidExpiry(): boolean {
        return this._isInvalid(this.formGroup, 'expiry', true);
    }

    get name() { return this._formGet(this.formGroup, 'name'); }
    get invalidName(): boolean {
        return this._isInvalid(this.formGroup, 'name', true);
    }

    get number() { return this._formGet(this.formGroup, 'number'); }
    get invalidNumber(): boolean {
        return this._isInvalid(this.formGroup, 'number', true);
    }

    get cvc() { return this._formGet(this.formGroup, 'cvc'); }
    get invalidCvc(): boolean {
        return this._isInvalid(this.formGroup, 'cvc', true);
    }

    protected formInit(): void {

        this.requireInvoice = false;

        // Reset for hide form errors
        this.touched = true;

        this.formGroup = new UntypedFormGroup({

            name: new UntypedFormControl(
                '',
                [Validators.required]
            ),
            number: new UntypedFormControl(
                '',
                [Validators.required, Validators.pattern('^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\\d{3})\\d{11})$')]
            ),
            expiry: new UntypedFormControl(
                '',
                [Validators.required]
            ),
            cvc: new UntypedFormControl(
                '',
                [Validators.required, Validators.pattern('^\\d{3}$')]
            ),
            billing: new UntypedFormControl(
                false,
            ),
        });

        this.dynamicInputValidation();
    }

    private dynamicInputValidation() {

        // Visa, MasterCard valid patterns
        this.number.setValidators([Validators.required, Validators.pattern('^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12})$')]);
        this.cvc.setValidators([Validators.required, Validators.pattern('^\\d{3}$')]);

        // Se valida que se el input de numero venga con información
        if (this.number.value) {

            const firstNumber = this.number.value.substring(0, 1);
            const firstSecondNumber = Number(this.number.value.substring(0, 2));

            // Se valida que tipo de tarjeta es Amex
            if (firstSecondNumber == 34 || firstSecondNumber == 37) {

                this.number.setValidators([Validators.required, Validators.pattern('^(?:3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\\d{3})\\d{11})$')]);
                this.cvc.setValidators([Validators.required, Validators.pattern('^\\d{4}$')]);
                // if (this.hideAmex) {
                //     this.number.setValidators([Validators.required, Validators.pattern('^(?:999900008888000077770000666600005555)$')]); // must be invalid
                // }

            } else if (firstNumber != '4' && !(firstSecondNumber >= 51 && firstSecondNumber <= 55) && !(firstSecondNumber == 60)) {

                // otros números diferente a Visa, MasterCard y Discovery, deben ser inválidos
                this.cvc.setValidators([Validators.required, Validators.pattern('^\\d{3,4}$')]);
                this.number.setValidators([Validators.required, Validators.pattern('^(?:999900008888000077770000666600005555)$')]); // must be invalid
            }
        }
        this.cvc.updateValueAndValidity();
        this.number.updateValueAndValidity();
    }

    // End Form
}
